import { Button } from 'components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import { Input } from 'components/ui/input';
import { TEST_IDS } from 'mocks/ids';
import { SubmitHandler, useForm } from 'react-hook-form';
import { BulkCreateReferencePayload } from 'modules/threads/types';
import { getFileTypeFromName } from 'pages/model/utils';
import { useModal } from 'modules/modals/ModalProvider';
import { InfoIcon } from 'lucide-react';
import { FileUploadType } from 'modules/app/modals/DuplicateFileVersion';
import useFileDuplicationHandler from 'hooks/useFileDuplicationHandler';
import { FILE_PARAMS } from '../../../../constants';
import { CopilotDataNotice } from '../common';
import { useMultiFileUpload } from './multifile-toast';

export const MultiFileForm = ({ threadId }: { threadId: string }) => {
  const { close } = useModal();
  const form = useForm<{ files: FileList }>();
  const { upload } = useMultiFileUpload(threadId);
  const { validateDuplicateFiles, showDuplicateFileModal } = useFileDuplicationHandler();

  const onSubmit: SubmitHandler<{ files: FileList }> = async ({ files }) => {
    const { uniqueFiles, duplicateFiles } = await validateDuplicateFiles(files, threadId);

    if (duplicateFiles.length) {
      showDuplicateFileModal(duplicateFiles, FileUploadType.MultipleFile, threadId);
    }

    const payloads: BulkCreateReferencePayload[] = uniqueFiles.map((file) => ({
      name: file.name,
      file,
      referenceType: getFileTypeFromName(file.name) !== undefined ? 'THREADS_3D_FILE' : 'FILE',
      thread: threadId,
      description: '',
    }));
    try {
      if (!duplicateFiles.length) close();
      if (uniqueFiles.length) {
        // Avoid `setTimeout` here.
        // In unit tests, leaked fake timers can prevent timeouts from firing, which makes uploads
        // appear to "never happen" and introduces CI-only flakes. A microtask keeps intent (defer
        // until after this tick) without depending on timers.
        Promise.resolve().then(() => {
          upload(payloads);
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 px-1"
        data-testid={TEST_IDS.add_reference_multifile_tab}
      >
        <FormField
          control={form.control}
          name="files"
          render={({ field: { onChange, value, ...field } }) => (
            <FormItem>
              <FormLabel>
                Files <span className="text-app-red">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  required
                  type="file"
                  className="cursor-pointer"
                  aria-label="file-input"
                  {...field}
                  onChange={(event) => onChange(event.target.files)}
                  size={FILE_PARAMS.SIZE}
                  multiple
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="mx-1 my-2 flex flex-row items-center gap-2 text-xs italic text-app-gray">
          <InfoIcon className="h-5 w-5" />
          - Files will be uploaded with their original filenames
          <br />- STL and STEP files will have Threads3D enabled
        </div>
        <CopilotDataNotice />
        <Button type="submit">Save</Button>
      </form>
    </Form>
  );
};
